import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

type PresentationState = 'active' | 'halfway' | 'interrupted' | 'completed';

export default function LivePresentation() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = (location.state as any)?.session;

  const [state, setState] = useState<PresentationState>('active');
  const [transcript, setTranscript] = useState<{ role: string; text: string }[]>([]);
  const [currentSpeaker, setCurrentSpeaker] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scores, setScores] = useState<any>(null);
  const [statusText, setStatusText] = useState('Connecting to your audience...');
  const [halfwayFeedback, setHalfwayFeedback] = useState<any>(null);
  const [interruptQuestion, setInterruptQuestion] = useState('');

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<any>(null);
  const transcriptRef = useRef('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const halfwayTriggeredRef = useRef(false);
  const stateRef = useRef<PresentationState>('active');
  const elapsedRef = useRef(0);

  const sessionLengthMinutes = session?.sessionLengthMinutes || 15;
  const allowInterruptions = session?.allowInterruptions || false;
  const enableHalfwayFeedback = session?.halfwayFeedback !== false;

  useEffect(() => {
    startPresentation();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pcRef.current) pcRef.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, currentSpeaker]);

  const startPresentation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/realtime/session-token`,
        { sessionId: session.sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await connectWebRTC(response.data.ephemeralToken);
    } catch {
      alert('Could not connect. Please try again.');
      navigate('/presentation-setup');
    }
  };

  const connectWebRTC = async (ephemeralToken: string) => {
    const pc = new RTCPeerConnection();
    pcRef.current = pc;

    const audio = new Audio();
    audio.autoplay = true;
    audioRef.current = audio;

    pc.ontrack = (event) => { audio.srcObject = event.streams[0]; };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;

    dc.onopen = () => {
      setStatusText('You are presenting — speak now');
      startTimer();

      const audiencePersonas: Record<string, string> = {
        board: 'You are a skeptical board member and CFO. Listen to the presentation. If interruptions are allowed, ask tough ROI and risk questions. Be direct and challenging. Never refer to the presenter as candidate — say you or the presenter.',
        investors: 'You are a VC investor. Listen to the presentation. Ask about market size, competition, traction, and burn rate if allowed to interrupt. Never refer to the presenter as candidate — say you or the presenter.',
        exec_team: 'You are a senior executive peer. Listen to the presentation. Ask about feasibility, resources, and team alignment if allowed to interrupt. Never refer to the presenter as candidate — say you or the presenter.',
        all_hands: 'You are an employee. Listen to the presentation. Ask about job security, culture impact, and what this means for your team if allowed to interrupt. Never refer to the presenter as candidate — say you or the presenter.',
        clients: 'You are a key client. Listen to the presentation. Ask about ROI, implementation timeline, and support if allowed to interrupt. Never refer to the presenter as candidate — say you or the presenter.',
      };

      const persona = audiencePersonas[session?.audienceType] || audiencePersonas.board;

      dc.send(JSON.stringify({
        type: 'session.update',
        session: {
          modalities: ['text', 'audio'],
          voice: 'alloy',
          instructions: `${persona} The presenter's topic is: ${session?.topic}. Their outline: ${session?.outline || 'Not provided'}. ${allowInterruptions ? 'You MAY interrupt with questions during the presentation.' : 'Do NOT interrupt. Wait until the end to ask questions.'} Always speak in English. Never call the presenter a candidate.`,
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.6,
            prefix_padding_ms: 300,
            silence_duration_ms: 800,
          },
        }
      }));

      dc.send(JSON.stringify({
        type: 'response.create',
        response: {
          modalities: ['text', 'audio'],
          instructions: 'Say only: "Whenever you are ready, please begin your presentation." Then stay silent and listen.'
        }
      }));
    };

    dc.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleEvent(data);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Proxy SDP exchange through our backend to avoid CORS issues
    const token = localStorage.getItem('token');
    const sdpResponse = await fetch(
      `${API_BASE_URL}/api/realtime/connect`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sdp: offer.sdp, ephemeralToken })
      }
    );
    const sdpData = await sdpResponse.json();
    if (!sdpData.answerSdp) throw new Error('No answer SDP from server');
    await pc.setRemoteDescription({ type: 'answer', sdp: sdpData.answerSdp });
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);
      const totalSeconds = sessionLengthMinutes * 60;

      if (enableHalfwayFeedback && !halfwayTriggeredRef.current && elapsedRef.current >= totalSeconds / 2) {
        halfwayTriggeredRef.current = true;
        clearInterval(timerRef.current);
        triggerHalfwayFeedback();
      } else if (elapsedRef.current >= totalSeconds) {
        clearInterval(timerRef.current);
        endSession();
      }
    }, 1000);
  };

  const handleEvent = (event: any) => {
    switch (event.type) {
      case 'response.audio_transcript.delta':
        setCurrentSpeaker(prev => prev + (event.delta || ''));
        setStatusText('Audience speaking...');
        break;

      case 'response.audio_transcript.done':
        if (event.transcript) {
          const text = event.transcript;
          if (text.includes('?') && allowInterruptions && stateRef.current === 'active') {
            setInterruptQuestion(text);
            setState('interrupted');
            stateRef.current = 'interrupted';
            setStatusText('Audience question — respond or defer');
          }
          setTranscript(prev => [...prev, { role: 'audience', text }]);
          transcriptRef.current += `\nAudience: ${text}`;
          setCurrentSpeaker('');
          if (stateRef.current === 'active') setStatusText('You are presenting — speak now');
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          setTranscript(prev => [...prev, { role: 'presenter', text: event.transcript }]);
          transcriptRef.current += `\nPresenter: ${event.transcript}`;
          if (stateRef.current === 'interrupted') {
            setState('active');
            stateRef.current = 'active';
          }
          setStatusText('You are presenting — speak now');
        }
        break;
    }
  };

  const triggerHalfwayFeedback = () => {
    setState('halfway');
    stateRef.current = 'halfway';
    setStatusText('Halfway point — coaching feedback');
    setHalfwayFeedback({
      message: `You are halfway through your ${sessionLengthMinutes}-minute presentation.`,
      tips: [
        'Check your pace — are you rushing through key points?',
        'Have you delivered your core message clearly yet?',
        'Is the audience engaged or are you losing their attention?'
      ]
    });
  };

  const handleHalfwayChoice = (choice: 'restart' | 'continue' | 'ignore') => {
    if (choice === 'restart') {
      navigate('/presentation-setup');
    } else {
      setState('active');
      stateRef.current = 'active';
      setHalfwayFeedback(null);
      if (choice === 'ignore') {
        halfwayTriggeredRef.current = true;
      }
      startTimer();
    }
  };

  const handleInterruptChoice = (choice: 'answer' | 'defer' | 'skip') => {
    setState('active');
    stateRef.current = 'active';
    setInterruptQuestion('');
    if (choice === 'defer') {
      setStatusText('Question deferred to end — continue presenting');
    } else if (choice === 'skip') {
      setStatusText('Question skipped — continue presenting');
    } else {
      setStatusText('Answer the question, then continue your presentation');
    }
  };

  const endSession = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pcRef.current) pcRef.current.close();
    setState('completed');
    stateRef.current = 'completed';
    setStatusText('Generating your scorecard...');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/realtime/complete`,
        {
          sessionId: session.sessionId,
          fullTranscript: transcriptRef.current,
          durationSeconds: elapsedRef.current,
          sessionType: 'presentation'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScores(response.data.scores);
    } catch {
      alert('Could not generate scorecard.');
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const remaining = (sessionLengthMinutes * 60) - elapsedSeconds;

  const getVerdict = (score: number) => {
    if (score >= 75) return { text: '✓ PRESENTATION READY', color: '#22C55E', bg: '#0A2010' };
    if (score >= 55) return { text: '⚡ NEEDS REFINEMENT', color: '#F59E0B', bg: '#1A1200' };
    return { text: '✗ NOT READY — PRACTICE MORE', color: '#EF4444', bg: '#1A0A0A' };
  };

  if (state === 'halfway' && halfwayFeedback) {
    return (
      <div style={styles.container}>
        <div style={styles.overlayCard}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.overlayLabel}>⏸ HALFWAY POINT</p>
          <h2 style={styles.overlayTitle}>Quick Coaching Check</h2>
          <p style={styles.overlayText}>{halfwayFeedback.message}</p>
          <div style={styles.tipsList}>
            {halfwayFeedback.tips.map((tip: string, i: number) => (
              <div key={i} style={styles.tipItem}>
                <span style={styles.tipDot}>→</span>
                <span style={styles.tipText}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={styles.choiceButtons}>
            <button style={styles.choiceButtonDanger} onClick={() => handleHalfwayChoice('restart')}>↺ Adjust & Restart</button>
            <button style={styles.choiceButtonPrimary} onClick={() => handleHalfwayChoice('continue')}>→ Continue with Feedback</button>
            <button style={styles.choiceButtonGhost} onClick={() => handleHalfwayChoice('ignore')}>Skip to End</button>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'completed') {
    if (!scores) {
      return (
        <div style={styles.container}>
          <div style={styles.overlayCard}>
            <p style={styles.brand}>VIRILITER</p>
            <p style={{ color: '#475569', textAlign: 'center', margin: 0 }}>Generating your scorecard...</p>
          </div>
        </div>
      );
    }

    const verdict = getVerdict(scores.overall_score);

    return (
      <div style={styles.container}>
        <div style={{ ...styles.overlayCard, maxWidth: '600px', gap: '16px' }}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.scoreHeadline}>{scores.session_headline}</p>
          <p style={styles.overallScore}>{scores.overall_score}<span style={styles.scoreSuffix}>/100</span></p>
          <div style={{ ...styles.verdictBox, backgroundColor: verdict.bg, borderColor: verdict.color }}>
            <span style={{ ...styles.verdictText, color: verdict.color }}>{verdict.text}</span>
          </div>
          <div style={styles.readinessTag}>
            <span style={styles.readinessText}>{scores.readiness_assessment?.toUpperCase().replace('_', ' ')}</span>
          </div>
          <div style={styles.scoresGrid}>
            {[
              ['Presence', scores.score_executive_presence],
              ['Clarity', scores.score_strategic_clarity],
              ['Structure', scores.score_structured_thinking],
              ['Ownership', scores.score_risk_ownership],
            ].map(([label, value]) => (
              <div key={label} style={styles.scoreItem}>
                <p style={styles.scoreValue}>{value}</p>
                <p style={styles.scoreLabel}>{label}</p>
              </div>
            ))}
          </div>
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>TOP STRENGTH</p>
            <p style={styles.feedbackText}>{scores.top_strength}</p>
          </div>
          <div style={{ ...styles.feedbackBlock, borderLeftColor: '#EF4444' }}>
            <p style={{ ...styles.feedbackLabel, color: '#EF4444' }}>CRITICAL GAP</p>
            <p style={styles.feedbackText}>{scores.critical_gap}</p>
          </div>
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>COACHING FEEDBACK</p>
            <p style={styles.feedbackText}>{scores.overall_coaching_feedback}</p>
          </div>
          <div style={styles.retryButtons}>
            <button style={styles.retryPrimary} onClick={() => navigate('/live-presentation', { state: { session } })}>↺ Try Again — Same Presentation</button>
            <button style={styles.retrySecondary} onClick={() => navigate('/presentation-setup')}>+ New Topic, Same Audience</button>
            <button style={styles.retryGhost} onClick={() => navigate('/presentation-setup')}>Start Fresh</button>
            <button style={styles.retryGhost} onClick={() => navigate('/home')}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.interviewContainer}>
      <div style={styles.interviewHeader}>
        <p style={styles.brand}>VIRILITER</p>
        <div style={styles.sessionInfo}>
          <span style={styles.sessionTopic}>{session?.topic}</span>
        </div>
        <div style={styles.timerBox}>
          <span style={{ ...styles.timer, color: remaining < 120 ? '#EF4444' : '#94A3B8' }}>
            {formatTime(remaining)}
          </span>
        </div>
      </div>

      <div style={styles.statusBar}>
        <div style={{
          ...styles.statusDot,
          backgroundColor: statusText.includes('Audience') ? '#22C55E' :
            statusText.includes('question') ? '#F59E0B' : '#3B82F6'
        }} />
        <span style={styles.statusText}>{statusText}</span>
      </div>

      {state === 'interrupted' && interruptQuestion && (
        <div style={styles.interruptBanner}>
          <p style={styles.interruptLabel}>🙋 AUDIENCE QUESTION</p>
          <p style={styles.interruptText}>{interruptQuestion}</p>
          <div style={styles.interruptButtons}>
            <button style={styles.interruptBtnPrimary} onClick={() => handleInterruptChoice('answer')}>Answer Now</button>
            <button style={styles.interruptBtnGhost} onClick={() => handleInterruptChoice('defer')}>Defer to End</button>
            <button style={styles.interruptBtnGhost} onClick={() => handleInterruptChoice('skip')}>Skip</button>
          </div>
        </div>
      )}

      <div ref={scrollRef} style={styles.transcriptContainer}>
        {transcript.map((item, i) => (
          <div key={i} style={{
            ...styles.message,
            ...(item.role === 'audience' ? styles.messageAudience : styles.messagePresenter)
          }}>
            <p style={styles.messageRole}>{item.role === 'audience' ? 'AUDIENCE' : 'YOU'}</p>
            <p style={styles.messageText}>{item.text}</p>
          </div>
        ))}
        {currentSpeaker && (
          <div style={{ ...styles.message, ...styles.messageAudience }}>
            <p style={styles.messageRole}>AUDIENCE</p>
            <p style={styles.messageText}>{currentSpeaker}<span style={{ color: '#3B82F6' }}>|</span></p>
          </div>
        )}
      </div>

      <div style={styles.controlBar}>
        <div style={styles.micIndicator}>
          <div style={{ ...styles.micDot, backgroundColor: state === 'active' ? '#3B82F6' : '#1E293B' }} />
        </div>
        <span style={styles.micLabel}>{statusText}</span>
        <button style={styles.endButton} onClick={endSession}>End Session</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  overlayCard: { width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  overlayLabel: { color: '#F59E0B', fontSize: '11px', letterSpacing: '3px', fontWeight: '700', margin: 0 },
  overlayTitle: { color: '#F1F5F9', fontSize: '24px', fontWeight: '800', textAlign: 'center', margin: 0 },
  overlayText: { color: '#475569', fontSize: '14px', textAlign: 'center', margin: 0 },
  tipsList: { width: '100%', backgroundColor: '#0F172A', borderRadius: '12px', padding: '16px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', gap: '10px' },
  tipItem: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  tipDot: { color: '#3B82F6', fontWeight: '700', flexShrink: 0 },
  tipText: { color: '#94A3B8', fontSize: '14px', lineHeight: '1.5' },
  choiceButtons: { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' },
  choiceButtonPrimary: { backgroundColor: '#3B82F6', color: '#fff', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  choiceButtonDanger: { backgroundColor: '#1A0A0A', color: '#EF4444', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: '1px solid #EF4444', cursor: 'pointer', width: '100%' },
  choiceButtonGhost: { backgroundColor: 'transparent', color: '#475569', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
  primaryButton: { backgroundColor: '#3B82F6', color: '#fff', padding: '18px', borderRadius: '12px', fontSize: '16px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  scoreHeadline: { color: '#94A3B8', fontSize: '14px', textAlign: 'center', margin: 0, lineHeight: '1.5' },
  overallScore: { color: '#F1F5F9', fontSize: '64px', fontWeight: '700', textAlign: 'center', margin: 0, letterSpacing: '-2px' },
  scoreSuffix: { fontSize: '20px', color: '#475569' },
  verdictBox: { borderRadius: '12px', padding: '14px 20px', border: '1px solid', textAlign: 'center', width: '100%', boxSizing: 'border-box' },
  verdictText: { fontSize: '14px', fontWeight: '800', letterSpacing: '1px' },
  readinessTag: { backgroundColor: '#1E3A5F', borderRadius: '20px', padding: '6px 16px', border: '1px solid #3B82F6' },
  readinessText: { color: '#3B82F6', fontSize: '11px', letterSpacing: '2px', fontWeight: '600' },
  scoresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%' },
  scoreItem: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #1E293B' },
  scoreValue: { color: '#3B82F6', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  scoreLabel: { color: '#475569', fontSize: '10px', letterSpacing: '1px', margin: 0 },
  feedbackBlock: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', border: '1px solid #1E293B', borderLeft: '3px solid #3B82F6', width: '100%', boxSizing: 'border-box' },
  feedbackLabel: { color: '#3B82F6', fontSize: '10px', letterSpacing: '2px', fontWeight: '600', margin: '0 0 8px 0' },
  feedbackText: { color: '#94A3B8', fontSize: '14px', lineHeight: '1.6', margin: 0 },
  retryButtons: { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' },
  retryPrimary: { backgroundColor: '#3B82F6', color: '#fff', padding: '18px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  retrySecondary: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
  retryGhost: { backgroundColor: 'transparent', color: '#475569', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
  interviewContainer: { height: '100vh', backgroundColor: '#0A0F1E', display: 'flex', flexDirection: 'column' },
  interviewHeader: { padding: '20px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sessionInfo: { flex: 1, textAlign: 'center' },
  sessionTopic: { color: '#475569', fontSize: '12px' },
  timerBox: { backgroundColor: '#0F172A', borderRadius: '8px', padding: '6px 12px', border: '1px solid #1E293B' },
  timer: { fontSize: '14px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' },
  statusBar: { display: 'flex', alignItems: 'center', gap: '8px', padding: '0 24px 12px' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  statusText: { color: '#475569', fontSize: '12px', letterSpacing: '1px' },
  interruptBanner: { margin: '0 16px 12px', backgroundColor: '#1A1200', borderRadius: '12px', padding: '16px', border: '1px solid #F59E0B' },
  interruptLabel: { color: '#F59E0B', fontSize: '10px', letterSpacing: '2px', fontWeight: '700', margin: '0 0 8px 0' },
  interruptText: { color: '#F1F5F9', fontSize: '14px', lineHeight: '1.5', margin: '0 0 12px 0' },
  interruptButtons: { display: 'flex', gap: '8px' },
  interruptBtnPrimary: { backgroundColor: '#3B82F6', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer' },
  interruptBtnGhost: { backgroundColor: 'transparent', color: '#475569', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer' },
  transcriptContainer: { flex: 1, overflowY: 'auto', padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  message: { borderRadius: '12px', padding: '14px', maxWidth: '80%' },
  messageAudience: { backgroundColor: '#0F172A', alignSelf: 'flex-start', border: '1px solid #1E2D4A' },
  messagePresenter: { backgroundColor: '#1E3A5F', alignSelf: 'flex-end' },
  messageRole: { color: '#3B82F6', fontSize: '9px', letterSpacing: '2px', margin: '0 0 6px 0' },
  messageText: { color: '#E2E8F0', fontSize: '14px', lineHeight: '1.5', margin: 0 },
  controlBar: { padding: '16px 24px 32px', display: 'flex', alignItems: 'center', gap: '16px', borderTop: '1px solid #1E293B' },
  micIndicator: { width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#0F172A', border: '1px solid #1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  micDot: { width: '16px', height: '16px', borderRadius: '50%' },
  micLabel: { flex: 1, color: '#475569', fontSize: '13px' },
  endButton: { backgroundColor: '#1A0A0A', color: '#EF4444', border: '1px solid #EF4444', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};